import { z } from 'zod/v4';

// Base capability runtypes
export const BooleanSpecificationCapabilityRuntype = z.object({
    allowed: z.array(z.boolean()).optional(),
    forbidden: z.array(z.boolean()).optional(),
    default: z.boolean().optional(),
});

export const NumericSpecificationCapabilityRuntype = z.object({
    min: z.number().optional(),
    max: z.number().optional(),
    allowed: z.array(z.number()).optional(),
    forbidden: z.array(z.number()).optional(),
    default: z.number().optional(),
    defaultUnit: z.string().optional(),
    disabled: z.boolean().optional(),
    // When set, this field's value is derived from (implied by) the named capability — e.g. the Tg value implied by
    // the selected base `materialNumber`. The frontend uses it to lock/annotate the field and keep its value in sync.
    impliedBy: z.string().optional(),
});

export const EnumSpecificationCapabilityRuntype = z.object({
    allowed: z.array(z.string()).optional(),
    forbidden: z.array(z.string()).optional(),
    default: z.string().optional(),
    disabled: z.boolean().optional(),
    // When set, this field's value is derived from (implied by) the named capability — e.g. the soldermask color
    // implied by the selected `soldermaskMaterialNumber`. The frontend uses it to lock/annotate the field and keep its
    // value in sync.
    impliedBy: z.string().optional(),
});

export const StringSpecificationCapabilityRuntype = z.object({
    allowed: z.array(z.string()).optional(),
    forbidden: z.array(z.string()).optional(),
    default: z.string().optional(),
});

// PCB capability runtypes
export const PCBV2BoardCapabilitiesRuntype = z.object({
    boardHeight: NumericSpecificationCapabilityRuntype,
    boardWidth: NumericSpecificationCapabilityRuntype,
    silkscreenColor: EnumSpecificationCapabilityRuntype,
    silkscreenSide: EnumSpecificationCapabilityRuntype,
    surfaceFinish: EnumSpecificationCapabilityRuntype,
    enigThickness: NumericSpecificationCapabilityRuntype,
    applicationType: EnumSpecificationCapabilityRuntype,
    hardGold: BooleanSpecificationCapabilityRuntype,
    hardGoldArea: NumericSpecificationCapabilityRuntype,
    hardGoldThickness: NumericSpecificationCapabilityRuntype,
    exposedCopperArea: NumericSpecificationCapabilityRuntype,
    exposedCopperAreaTop: NumericSpecificationCapabilityRuntype,
    exposedCopperAreaBottom: NumericSpecificationCapabilityRuntype,
    traceWidth: NumericSpecificationCapabilityRuntype,
    outerTraceWidth: NumericSpecificationCapabilityRuntype,
    innerTraceWidth: NumericSpecificationCapabilityRuntype,
    copperClearance: NumericSpecificationCapabilityRuntype,
    outerCopperClearance: NumericSpecificationCapabilityRuntype,
    innerCopperClearance: NumericSpecificationCapabilityRuntype,
    soldermaskColor: EnumSpecificationCapabilityRuntype,
    soldermaskSide: EnumSpecificationCapabilityRuntype,
    soldermaskDam: NumericSpecificationCapabilityRuntype,
    soldermaskClearance: NumericSpecificationCapabilityRuntype,
    ipc600Class: EnumSpecificationCapabilityRuntype,
    eTest: BooleanSpecificationCapabilityRuntype,
    pressFit: BooleanSpecificationCapabilityRuntype,
    pressFitTechnology: EnumSpecificationCapabilityRuntype,
    impedanceTested: BooleanSpecificationCapabilityRuntype,
    impedanceTolerance: NumericSpecificationCapabilityRuntype,
    peelableMask: EnumSpecificationCapabilityRuntype,
    captonTape: EnumSpecificationCapabilityRuntype,
    reports: EnumSpecificationCapabilityRuntype,
    itar: BooleanSpecificationCapabilityRuntype,
    carbonPrint: BooleanSpecificationCapabilityRuntype,
    carbonPrintSide: EnumSpecificationCapabilityRuntype,
    edgeMetalization: BooleanSpecificationCapabilityRuntype,
    flammabilityRating: EnumSpecificationCapabilityRuntype,
    ctiClass: EnumSpecificationCapabilityRuntype,
    plcClass: EnumSpecificationCapabilityRuntype,
    maxXOutsAllowed: NumericSpecificationCapabilityRuntype,
    halogenFree: BooleanSpecificationCapabilityRuntype,
    placementSide: EnumSpecificationCapabilityRuntype,
    ecobond: BooleanSpecificationCapabilityRuntype,
    numberOfLines: NumericSpecificationCapabilityRuntype,
    halfCutPlatedVias: BooleanSpecificationCapabilityRuntype,
    // Soldermask material vendor/number options sourced from the central material catalog (T3b).
    // `soldermaskMaterialVendor.allowed` lists all vendors; `soldermaskMaterialNumber.allowed`
    // lists the material numbers of the currently selected vendor.
    soldermaskMaterialVendor: EnumSpecificationCapabilityRuntype,
    soldermaskMaterialNumber: EnumSpecificationCapabilityRuntype,
});

export const PCBV2LayerStackCapabilitiesRuntype = z.object({
    layerstackType: EnumSpecificationCapabilityRuntype,
    layerstackThicknessTolerance: NumericSpecificationCapabilityRuntype,
    ulLayerStack: BooleanSpecificationCapabilityRuntype,
    ulMarkingType: EnumSpecificationCapabilityRuntype,
    layercount: NumericSpecificationCapabilityRuntype,
    finalThickness: NumericSpecificationCapabilityRuntype,
    baseMaterial: EnumSpecificationCapabilityRuntype,
    outerCopperThickness: NumericSpecificationCapabilityRuntype,
    innerCopperThickness: NumericSpecificationCapabilityRuntype,
    minOuterLayerStructure: NumericSpecificationCapabilityRuntype,
    minInnerLayerStructure: NumericSpecificationCapabilityRuntype,
    rohsCompilant: BooleanSpecificationCapabilityRuntype,
    tgValue: NumericSpecificationCapabilityRuntype,
    numberOfPrepregs: NumericSpecificationCapabilityRuntype,
    numberOfLaminationCycles: NumericSpecificationCapabilityRuntype,
    // Base material vendor/number options sourced from the central material catalog (T3).
    // `materialVendor.allowed` lists all vendors; `materialNumber.allowed` lists the material
    // numbers of the currently selected vendor.
    materialVendor: EnumSpecificationCapabilityRuntype,
    materialNumber: EnumSpecificationCapabilityRuntype,
});

export const PCBV2MechanicalCapabilitiesRuntype = z.object({
    minViaDiameter: NumericSpecificationCapabilityRuntype,
    viaFilling: EnumSpecificationCapabilityRuntype,
    blindVias: BooleanSpecificationCapabilityRuntype,
    buriedVias: BooleanSpecificationCapabilityRuntype,
    chamfering: EnumSpecificationCapabilityRuntype,
    aspectRatio: NumericSpecificationCapabilityRuntype,
    totalDrillCount: NumericSpecificationCapabilityRuntype,
    microVias: BooleanSpecificationCapabilityRuntype,
    phCount: NumericSpecificationCapabilityRuntype,
    phToolCount: NumericSpecificationCapabilityRuntype,
    nphCount: NumericSpecificationCapabilityRuntype,
    phMinSize: NumericSpecificationCapabilityRuntype,
    nphMaxSize: NumericSpecificationCapabilityRuntype,
    phMaxSize: NumericSpecificationCapabilityRuntype,
    nphToolCount: NumericSpecificationCapabilityRuntype,
    phAnnularRing: NumericSpecificationCapabilityRuntype,
    phAnnularRingBreakouts: NumericSpecificationCapabilityRuntype,
    nphMinSize: NumericSpecificationCapabilityRuntype,
    phSurfaceArea: NumericSpecificationCapabilityRuntype,
});

export const PCBV2ReportsCapabilitiesRuntype = z.object({
    crossSection: BooleanSpecificationCapabilityRuntype,
    xRayMeasurement: BooleanSpecificationCapabilityRuntype,
    xRayMeasurementPoints: NumericSpecificationCapabilityRuntype,
    firstArticleInspection: BooleanSpecificationCapabilityRuntype,
    certificateOfConformance: BooleanSpecificationCapabilityRuntype,
});

export const PCBV2MarkingsCapabilitiesRuntype = z.object({
    dateCode: EnumSpecificationCapabilityRuntype,
    fabricatorLogo: BooleanSpecificationCapabilityRuntype,
    datamatrixCode: BooleanSpecificationCapabilityRuntype,
});

export const PCBV2MiscellaneousCapabilitiesRuntype = z.object({
    manufacturingInformation: StringSpecificationCapabilityRuntype,
});

export const PCBV2SpecificationCapabilitiesRuntype = z.object({
    board: PCBV2BoardCapabilitiesRuntype,
    layerStack: PCBV2LayerStackCapabilitiesRuntype,
    mechanical: PCBV2MechanicalCapabilitiesRuntype,
    reports: PCBV2ReportsCapabilitiesRuntype,
    markings: PCBV2MarkingsCapabilitiesRuntype,
    miscellaneous: PCBV2MiscellaneousCapabilitiesRuntype,
});

// Create capability name runtype from the existing capability runtypes
export const PcbCapabilityNameRuntype = z.union([
    // Board capabilities
    z.literal('boardHeight'),
    z.literal('boardWidth'),
    z.literal('silkscreenColor'),
    z.literal('silkscreenSide'),
    z.literal('surfaceFinish'),
    z.literal('enigThickness'),
    z.literal('applicationType'),
    z.literal('hardGold'),
    z.literal('hardGoldArea'),
    z.literal('hardGoldThickness'),
    z.literal('exposedCopperArea'),
    z.literal('exposedCopperAreaTop'),
    z.literal('exposedCopperAreaBottom'),
    z.literal('traceWidth'),
    z.literal('outerTraceWidth'),
    z.literal('innerTraceWidth'),
    z.literal('copperClearance'),
    z.literal('outerCopperClearance'),
    z.literal('innerCopperClearance'),
    z.literal('soldermaskColor'),
    z.literal('soldermaskSide'),
    z.literal('soldermaskDam'),
    z.literal('soldermaskClearance'),
    z.literal('ipc600Class'),
    z.literal('eTest'),
    z.literal('pressFit'),
    z.literal('pressFitTechnology'),
    z.literal('impedanceTested'),
    z.literal('impedanceTolerance'),
    z.literal('peelableMask'),
    z.literal('captonTape'),
    z.literal('reports'),
    z.literal('itar'),
    z.literal('carbonPrint'),
    z.literal('carbonPrintSide'),
    z.literal('edgeMetalization'),
    z.literal('flammabilityRating'),
    z.literal('ctiClass'),
    z.literal('plcClass'),
    z.literal('maxXOutsAllowed'),
    z.literal('halogenFree'),
    z.literal('placementSide'),
    z.literal('ecobond'),
    z.literal('numberOfLines'),
    z.literal('halfCutPlatedVias'),

    // LayerStack capabilities
    z.literal('layerstackType'),
    z.literal('layerstackThicknessTolerance'),
    z.literal('ulLayerStack'),
    z.literal('ulMarkingType'),
    z.literal('layercount'),
    z.literal('finalThickness'),
    z.literal('baseMaterial'),
    z.literal('outerCopperThickness'),
    z.literal('innerCopperThickness'),
    z.literal('minOuterLayerStructure'),
    z.literal('minInnerLayerStructure'),
    z.literal('rohsCompilant'),
    z.literal('tgValue'),
    z.literal('numberOfPrepregs'),
    z.literal('numberOfLaminationCycles'),

    // Mechanical capabilities
    z.literal('minViaDiameter'),
    z.literal('viaFilling'),
    z.literal('blindVias'),
    z.literal('buriedVias'),
    z.literal('chamfering'),
    z.literal('aspectRatio'),
    z.literal('totalDrillCount'),
    z.literal('microVias'),
    z.literal('phCount'),
    z.literal('phToolCount'),
    z.literal('nphCount'),
    z.literal('phMinSize'),
    z.literal('nphMaxSize'),
    z.literal('phMaxSize'),
    z.literal('nphToolCount'),
    z.literal('phAnnularRing'),
    z.literal('phAnnularRingBreakouts'),
    z.literal('nphMinSize'),
    z.literal('phSurfaceArea'),

    // Reports capabilities
    z.literal('crossSection'),
    z.literal('xRayMeasurement'),
    z.literal('xRayMeasurementPoints'),
    z.literal('firstArticleInspection'),
    z.literal('certificateOfConformance'),

    // Markings capabilities
    z.literal('dateCode'),
    z.literal('fabricatorLogo'),
    z.literal('datamatrixCode'),

    // Miscellaneous capabilities
    z.literal('manufacturingInformation'),

    // Material capabilities
    z.literal('materialVendor'),
    z.literal('materialNumber'),
    z.literal('soldermaskMaterialVendor'),
    z.literal('soldermaskMaterialNumber'),
]);

// Type exports
export type CapabilityNameDTO = z.infer<typeof PcbCapabilityNameRuntype>;
export interface PCBV2SpecificationCapabilities extends z.infer<typeof PCBV2SpecificationCapabilitiesRuntype> {}
